import { Stack } from '@mui/material';
import { useSnapshot } from 'valtio';
import PoliciesStore from '../../store/policies';
import { IArgs } from '../../types/policies-types';
import BooleanField from './BooleanField';
import MultiSelectStringArrayField from './MultiSelectStringArrayField';
import NumberField from './NumberField';
import StringArrayField from './StringArrayField';
import StringField from './StringField';
import StringTagsArrayField from './StringTagsArrayField';

interface DynamicArgsProps {
  args: Array<IArgs>;
  policyId: string;
}

const DynamicArgs: React.FC<React.PropsWithChildren<DynamicArgsProps>> = ({
  policyId,
  args,
}) => {
  const { dirtyPolicies } = useSnapshot(PoliciesStore);

  return (
    <Stack flexGrow={1} flexWrap='wrap' direction='row'>
      {(dirtyPolicies?.[policyId]?.args || args).map(arg => {
        const Component = createDynamicArgsInput(arg, policyId);
        return <Component key={arg.id} {...arg} />;
      })}
    </Stack>
  );
};

function createDynamicArgsInput(
  { type, visible, multiSelect = false, ...restArgs }: IArgs,
  policyId: string,
): React.FC<React.PropsWithChildren<IArgs>> {
  const DynamicArgsComponent = () => {
    if (!visible) return null;
    switch (type) {
      case 'string':
        return <StringField policyId={policyId} {...restArgs} />;
      case 'number':
        return <NumberField policyId={policyId} {...restArgs} />;
      case 'stringArray':
        return multiSelect ? (
          <MultiSelectStringArrayField policyId={policyId} {...restArgs} />
        ) : restArgs.range && restArgs.range.length > 0 ? (
          <StringArrayField policyId={policyId} {...restArgs} />
        ) : (
          <StringTagsArrayField policyId={policyId} {...restArgs} />
        );
      case 'boolean':
        return <BooleanField policyId={policyId} {...restArgs} />;
      default:
        throw new Error('component does not exists');
    }
  };
  return DynamicArgsComponent;
}

export default DynamicArgs;
